import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { chain, zipObject, isEmpty } from 'lodash';
import { Container } from '@fiverr-private/layout_components';
import { GigServiceCard } from '../GigServiceCard';
import { CustomOrderCard } from '../CustomOrderCard';
import { biEnrichmentType, customOrderPropsType, gigType } from '../GigServiceCard/propTypes';

import './index.scss';
import { currencyType } from '../../../shared/types';

const LAYOUT = {
    GRID: 'GRID',
    LIST_HORIZONTAL: 'LIST_HORIZONTAL',
};

export const GigServiceCardListings = ({
    className,
    withBigGridCell,
    withCustomOrder,
    customOrderProps,
    items = [],
    relevantGigIds = [],
    fiverrChoice,
    sellerUsername,
    sellerDisplayName,
    sellerImage,
    showTranslatedUGC,
    isUserPending,
    openUserActivateModal,
    openInNewTab = false,
    layout = LAYOUT.GRID,
    currency,
    source,
    biEnrichment = {},
    renderCustomCard,
}) => {
    const isGrid = layout === LAYOUT.GRID;
    const relevantGigIdsMap = useMemo(
        () => zipObject(relevantGigIds, Array(relevantGigIds.length).fill(true)),
        [relevantGigIds]
    );

    const orderedItems = useMemo(() => {
        if (isEmpty(relevantGigIds)) {
            return items;
        }

        const stringIds = relevantGigIds.map(String);
        const relevantGigs = chain(items)
            .filter((gig) => stringIds.includes(String(gig.gig_id)))
            .orderBy((gig) => stringIds.indexOf(String(gig.gig_id)), 'asc')
            .value();

        const commonGigs = items.filter((gig) => !stringIds.includes(String(gig.gig_id)));
        return [...relevantGigs, ...commonGigs];
    }, [items, relevantGigIds]);

    const classNames = classnames(className, 'gig-service-card-listings', {
        'layout-grid': layout === LAYOUT.GRID,
        'layout-list-horizontal': layout === LAYOUT.LIST_HORIZONTAL,
    });

    const customCard = renderCustomCard?.();

    return (
        <div className={classNames}>
            {orderedItems.map((gig, idx) => (
                <GigServiceCard
                    biEnrichment={biEnrichment}
                    key={gig.gig_id}
                    position={idx + 1}
                    className={classnames('card-layout', {
                        'card-big-cell': withBigGridCell && idx === 0,
                    })}
                    fiverrChoice={fiverrChoice}
                    isVertical={!isGrid}
                    isRelevant={!!relevantGigIdsMap[gig.gig_id] && idx === 0}
                    isBig={withBigGridCell && idx === 0}
                    gig={gig}
                    showTranslatedUGC={showTranslatedUGC}
                    openInNewTab={openInNewTab}
                    currency={currency}
                    source={source}
                />
            ))}
            {withCustomOrder && (
                <CustomOrderCard
                    className="card-layout"
                    sellerUsername={sellerUsername}
                    sellerDisplayName={sellerDisplayName}
                    sellerImage={sellerImage}
                    isVertical={!isGrid}
                    openUserActivateModal={openUserActivateModal}
                    isUserPending={isUserPending}
                    gigs={items}
                    currency={currency}
                    source={source}
                    customOrderProps={customOrderProps}
                />
            )}
            {customCard && <Container className="card-layout">{customCard}</Container>}
        </div>
    );
};

GigServiceCardListings.LAYOUT = LAYOUT;

GigServiceCardListings.propTypes = {
    biEnrichment: biEnrichmentType,
    className: PropTypes.string,
    items: PropTypes.arrayOf(gigType),
    relevantGigIds: PropTypes.array,
    fiverrChoice: PropTypes.bool,
    sellerUsername: PropTypes.string,
    sellerImage: PropTypes.string,
    withBigGridCell: PropTypes.bool,
    withCustomOrder: PropTypes.bool,
    customOrderProps: customOrderPropsType,
    showTranslatedUGC: PropTypes.bool,
    isUserPending: PropTypes.bool,
    sellerDisplayName: PropTypes.string,
    openUserActivateModal: PropTypes.func,
    openInNewTab: PropTypes.bool,
    layout: PropTypes.oneOf(Object.values(LAYOUT)),
    currency: currencyType,
    source: PropTypes.string,
    renderCustomCard: PropTypes.func,
};
